import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["pdfView", "reportView", "viewerRoot", "pdfContainer", "pageCounter", "pageControls", "pdfFileSelect"];
  static values = { pdfUrl: String, pdfFiles: Array };

  connect() {
    this._currentPage = 1;
    this._pageCount = 1;
    this._onKeydown = this._onKeydown.bind(this);
    document.addEventListener("keydown", this._onKeydown);
    this._bindEvents();
    this._initViewer();
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown);
    this._teardownControlsWheelBlock();
    this._teardownGrabToScroll();
    this._teardownScrollGuard();
    this._unbindEvents();
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }

  showReport() {
    this.pdfViewTarget.classList.add("hidden");
    this.pdfViewTarget.classList.remove("flex");
    this.reportViewTarget.classList.remove("hidden");
    this.reportViewTarget.classList.add("flex");
    document.dispatchEvent(new CustomEvent("qc-preview:view-changed", { detail: { view: "report" } }));
  }

  showPdf() {
    this.reportViewTarget.classList.add("hidden");
    this.reportViewTarget.classList.remove("flex");
    this.pdfViewTarget.classList.remove("hidden");
    this.pdfViewTarget.classList.add("flex");
    document.dispatchEvent(new CustomEvent("qc-preview:view-changed", { detail: { view: "pdf" } }));

    if (this.viewer?.viewer) {
      this.viewer.viewer.setScale(this.viewer.viewer.getScale());
    }
  }

  switchPdf(event) {
    const url = event.target.value;
    if (!url || url === this.pdfUrlValue) return;

    this.pdfUrlValue = url;
    this._destroyViewer();
    this._initViewer();
  }

  nextPage() {
    if (this._currentPage < this._pageCount) {
      this._navigateToPage(this._currentPage + 1);
    }
  }

  prevPage() {
    if (this._currentPage > 1) {
      this._navigateToPage(this._currentPage - 1);
    }
  }

  zoomIn() {
    if (!this.viewer?.viewer) return;
    const current = this.viewer.viewer.getScale();
    this.viewer.viewer.setScale(Math.min(current * 1.25, 5));
  }

  zoomOut() {
    if (!this.viewer?.viewer) return;
    const current = this.viewer.viewer.getScale();
    this.viewer.viewer.setScale(Math.max(current / 1.25, 0.25));
  }

  _bindEvents() {
    this._handlers = {
      "qc-pdf:fitToWidth": () => {
        this.viewer?.viewer?.setScale("page-width");
      },
      "qc-pdf:download": () => this.viewer?.download(),
      "qc-pdf:search": (e) => this.viewer?.findController?.find(e.detail.query),
      "qc-pdf:findNext": () => this.viewer?.findController?.findNext(),
      "qc-pdf:findPrevious": () => this.viewer?.findController?.findPrevious(),
      "qc-pdf:clearSearch": () => this.viewer?.findController?.find(""),
      "pdf-viewer:ready": (e) => this._onViewerReady(e),
      "pdf-viewer:page-changed": (e) => this._onPageChanged(e),
    };
    Object.entries(this._handlers).forEach(([event, handler]) =>
      this.element.addEventListener(event, handler)
    );
  }

  _unbindEvents() {
    if (!this._handlers) return;
    Object.entries(this._handlers).forEach(([event, handler]) =>
      this.element.removeEventListener(event, handler)
    );
    this._handlers = null;
  }

  async _initViewer() {
    if (!this.hasViewerRootTarget || !this.pdfUrlValue) return;

    const { PdfViewer } = await import("stimulus-pdf-viewer");
    const pdfFile = this.pdfFilesValue.find((f) => f.url === this.pdfUrlValue);
    this.viewer = new PdfViewer(this.viewerRootTarget, {
      documentUrl: this.pdfUrlValue,
      documentName: pdfFile?.name,
    });

    const originalCallback = this.viewer.findController.onUpdateState;
    this.viewer.findController.onUpdateState = (state, matchInfo) => {
      originalCallback(state, matchInfo);
      this.element.dispatchEvent(
        new CustomEvent("qc-pdf:searchUpdate", {
          detail: { current: matchInfo.current || 0, total: matchInfo.total || 0 },
        })
      );
    };

    try {
      await this.viewer.load();
    } catch (error) {
      console.error("[qc-preview-panel] Failed to load PDF:", error);
    }
  }

  _destroyViewer() {
    this._teardownControlsWheelBlock();
    this._teardownGrabToScroll();
    this._teardownScrollGuard();
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
    this._currentPage = 1;
    this._pageCount = 1;
    this._updatePageCounter();
  }

  _onViewerReady(e) {
    this._pageCount = e.detail.pageCount;
    this._currentPage = e.detail.currentPage || 1;
    this._updatePageCounter();
    this._setupScrollGuard();
    this._setupGrabToScroll();
    this._setupControlsWheelBlock();
  }

  _onPageChanged(e) {
    this._currentPage = e.detail.currentPage;
    this._pageCount = e.detail.pageCount;
    this._updatePageCounter();
  }

  _navigateToPage(pageNumber) {
    if (!this.viewer) return;
    this.viewer.goToPage(pageNumber);
    this._currentPage = pageNumber;
    this._updatePageCounter();
  }

  _updatePageCounter() {
    if (this.hasPageCounterTarget) {
      this.pageCounterTarget.textContent = `Page ${this._currentPage} of ${this._pageCount}`;
    }
  }

  _setupScrollGuard() {
    const container = this._getScrollContainer();
    if (!container) return;
    this._scrollGuardHandler = this._clampVerticalScroll.bind(this);
    container.addEventListener("scroll", this._scrollGuardHandler);
  }

  _teardownScrollGuard() {
    const container = this._getScrollContainer();
    if (container && this._scrollGuardHandler) {
      container.removeEventListener("scroll", this._scrollGuardHandler);
    }
    this._scrollGuardHandler = null;
  }

  _clampVerticalScroll() {
    const container = this._getScrollContainer();
    if (!container) return;
    const lastPage = container.querySelector(".pdf-page:last-child");
    if (!lastPage) return;
    const navBarClearance = this.hasPageControlsTarget
      ? this.pageControlsTarget.offsetHeight + 32
      : 80;
    const maxScroll =
      lastPage.offsetTop + lastPage.offsetHeight + navBarClearance - container.clientHeight;
    if (maxScroll > 0 && container.scrollTop > maxScroll) {
      container.scrollTop = maxScroll;
    }
  }

  _setupGrabToScroll() {
    const container = this._getScrollContainer();
    if (!container) return;
    this._grabState = { isDown: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 };

    this._onGrabMouseDown = (e) => {
      if (e.target.closest(".textLayer") && e.target.tagName === "SPAN") return;
      this._grabState.isDown = true;
      this._grabState.startX = e.pageX;
      this._grabState.startY = e.pageY;
      this._grabState.scrollLeft = container.scrollLeft;
      this._grabState.scrollTop = container.scrollTop;
      container.classList.add("cursor-grabbing", "select-none");
    };

    this._onGrabMouseMove = (e) => {
      if (!this._grabState.isDown) return;
      e.preventDefault();
      container.scrollLeft = this._grabState.scrollLeft - (e.pageX - this._grabState.startX);
      container.scrollTop = this._grabState.scrollTop - (e.pageY - this._grabState.startY);
    };

    this._onGrabMouseUp = () => {
      if (!this._grabState.isDown) return;
      this._grabState.isDown = false;
      container.classList.remove("cursor-grabbing", "select-none");
    };

    container.addEventListener("mousedown", this._onGrabMouseDown);
    container.addEventListener("mousemove", this._onGrabMouseMove);
    container.addEventListener("mouseup", this._onGrabMouseUp);
    container.addEventListener("mouseleave", this._onGrabMouseUp);
  }

  _teardownGrabToScroll() {
    const container = this._getScrollContainer();
    if (!container) return;
    if (this._onGrabMouseDown) {
      container.removeEventListener("mousedown", this._onGrabMouseDown);
      container.removeEventListener("mousemove", this._onGrabMouseMove);
      container.removeEventListener("mouseup", this._onGrabMouseUp);
      container.removeEventListener("mouseleave", this._onGrabMouseUp);
    }
    this._grabState = null;
  }

  _setupControlsWheelBlock() {
    if (!this.hasPageControlsTarget) return;
    this._onControlsWheel = (e) => e.preventDefault();
    this.pageControlsTarget.addEventListener("wheel", this._onControlsWheel, { passive: false });
  }

  _teardownControlsWheelBlock() {
    if (this.hasPageControlsTarget && this._onControlsWheel) {
      this.pageControlsTarget.removeEventListener("wheel", this._onControlsWheel);
    }
    this._onControlsWheel = null;
  }

  _getScrollContainer() {
    return this.viewer?.viewer?.container;
  }

  _onKeydown(e) {
    if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
    const active = document.activeElement;
    const tag = active?.tagName?.toLowerCase();
    if (tag === "textarea" || tag === "select" || (tag === "input" && !["radio", "checkbox"].includes(active?.type))) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.nextPage();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.prevPage();
    }
  }
}
